import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import { v4 as UUID } from 'uuid'
import { Close } from '@mui/icons-material'
import { Breakpoint, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from '@mui/material'

const useStyles = makeStyles()(_ => ({
  icon: {
    color: 'black',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  stableGutters: {
    '& .MuiDialogContent-root': {
      scrollbarGutter: 'stable',
    },
  },
  // On my screen a fontWeight of 600 makes me think the title is quite blurry
  title: {
    fontSize: '24px',
    fontWeight: 500,
    color: 'black',
    lineHeight: '35px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '70%',
  },
  // if no title we can move the title in the component a bit up
  noTitle: {
    '& .MuiDialogContent-root .MuiDialogTitle-root': {
      position: 'absolute',
      top: 15,
    },
  },
  rootTitle: {
    '& not(.MuiDialogContent-root) .MuiDialogTitle-root': {
      paddingBottom: '10px',
    },
    '& .MuiDialogContent-root': {
      paddingTop: '10px',
    },
  },
}))

interface IGenericDialogProps {
  title: string
  open: boolean
  onClose: () => void
  width?: string | number
  maxWidth?: false | Breakpoint
  fullWidth?: boolean
  disableAutoClose?: boolean
  disableEscClose?: boolean
  actions?: React.ReactNode[]
  removedCloseButton?: boolean
  container?: HTMLElement
  stableGutters?: boolean
  customComponentInTitle?: React.ReactNode
  fullScreen?: boolean
}
export const GenericDialog: React.FC<IGenericDialogProps> = ({
  title,
  open,
  onClose,
  maxWidth,
  disableAutoClose,
  disableEscClose,
  actions,
  fullWidth = true,
  removedCloseButton,
  children,
  container = null,
  stableGutters = true,
  customComponentInTitle,
  fullScreen = false,
}) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  return (
    <Dialog
      className={`${stableGutters ? classes.stableGutters : ''} ${classes.rootTitle} ${title ? '' : classes.noTitle} `}
      open={open}
      onClose={(e, reason) => {
        if (disableAutoClose && reason === 'backdropClick') return
        onClose()
      }}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      disableEscapeKeyDown={disableEscClose}
      maxWidth={maxWidth}
      container={container}
      //onBackdropClick={_ => {}}
    >
      <CustomDialogTitle onClose={onClose} customComponent={customComponentInTitle}>
        {title}
      </CustomDialogTitle>
      <DialogContent>{children}</DialogContent>
      {(!removedCloseButton || actions) && (
        <DialogActions>
          {[
            !removedCloseButton ? (
              <Button key={UUID()} onClick={onClose} variant="text">
                {t('cancel', { ns: 'common' })}
              </Button>
            ) : (
              <></>
            ),
            ...(actions ? actions : []),
          ]}
        </DialogActions>
      )}
    </Dialog>
  )
}

export interface DialogTitleProps {
  children?: React.ReactNode
  onClose?: () => void
  customComponent?: React.ReactNode
}

export const CustomDialogTitle: React.FC<DialogTitleProps> = props => {
  const { children, onClose, customComponent, ...other } = props
  const { classes } = useStyles()

  return (
    <DialogTitle className="border-b padding-8" {...other}>
      <Grid container display={'flex'} padding={'6px 0 0 6px'} justifyContent={'space-between'}>
        <span className={classes.title}>{children}</span>
        <Grid container display={'flex'} alignItems={'center'} gap={1}>
          {customComponent && <>{customComponent}</>}
          {onClose ? (
            <IconButton aria-label="close" onClick={onClose} className={classes.icon}>
              <Close style={{ height: '25px', width: '25px' }} />
            </IconButton>
          ) : null}
        </Grid>
      </Grid>
    </DialogTitle>
  )
}
